import { useState, forwardRef } from "react";
import { Calendar, Clock } from "lucide-react";
import styles from "./DateTimePickers.module.css";

const formatDateStr = (d) => {
  if (!d) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const DatePicker = forwardRef(
  (
    {
      value = "",
      onChange,
      name,
      id,
      min,
      max,
      presets = false,
      disabled = false,
      error = false,
      className = "",
      ...props
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);

    const handlePreset = (daysOffset) => {
      const d = new Date();
      d.setDate(d.getDate() + daysOffset);
      const str = formatDateStr(d);
      if (onChange) {
        onChange({ target: { name, value: str } });
      }
    };

    const inputBoxClass = [
      styles.inputBox,
      isFocused ? styles.focused : "",
      error ? styles.hasError : "",
      disabled ? styles.disabled : "",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div className={styles.pickerWrapper}>
        <div className={inputBoxClass}>
          <span className={styles.iconSlot}>
            <Calendar size={16} />
          </span>
          <input
            ref={ref}
            type="date"
            id={id || name}
            name={name}
            value={value}
            onChange={onChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            min={min}
            max={max}
            disabled={disabled}
            className={styles.nativeInput}
            {...props}
          />
        </div>

        {presets && !disabled && (
          <div className={styles.presetsRow}>
            <button
              type="button"
              className={styles.presetPill}
              onClick={() => handlePreset(0)}
            >
              Today
            </button>
            <button
              type="button"
              className={styles.presetPill}
              onClick={() => handlePreset(1)}
            >
              Tomorrow
            </button>
            <button
              type="button"
              className={styles.presetPill}
              onClick={() => handlePreset(7)}
            >
              Next Week
            </button>
          </div>
        )}
      </div>
    );
  }
);

DatePicker.displayName = "DatePicker";

export const TimePicker = forwardRef(
  (
    {
      value = "",
      onChange,
      name,
      id,
      step = "60",
      disabled = false,
      error = false,
      className = "",
      ...props
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);

    const inputBoxClass = [
      styles.inputBox,
      isFocused ? styles.focused : "",
      error ? styles.hasError : "",
      disabled ? styles.disabled : "",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div className={styles.pickerWrapper}>
        <div className={inputBoxClass}>
          <span className={styles.iconSlot}>
            <Clock size={16} />
          </span>
          <input
            ref={ref}
            type="time"
            id={id || name}
            name={name}
            value={value}
            onChange={onChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            step={step}
            disabled={disabled}
            className={styles.nativeInput}
            {...props}
          />
        </div>
      </div>
    );
  }
);

TimePicker.displayName = "TimePicker";

export default DatePicker;
